import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, Users, Calendar, Image as ImageIcon, MessageCircle,
  TrendingUp, AlertTriangle, MapPin, Award, RefreshCw, Activity as ActivityIcon
} from 'lucide-react';
import api from '../lib/api';

/**
 * AdminAnalytics — single-page business overview.
 * Pulls /api/admin/analytics (one aggregate endpoint) and renders KPI cards,
 * 30-day sparklines, and top lists. Premium minimal ASAP look.
 */
export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/admin/me');
        if (alive) setIsAdmin(!!r.data.is_admin);
      } catch { setIsAdmin(false); }
      setAuthChecking(false);
    })();
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/analytics');
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin]);

  if (authChecking) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex items-center justify-center">
        <RefreshCw size={20} className="text-[#9B9EA3] animate-spin" />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="min-h-[100dvh] bg-[#F7F6F2] flex flex-col items-center justify-center px-6 text-center">
        <Shield size={28} className="text-[#FF3B30] mb-3" />
        <h1 className="text-lg font-extrabold text-[#111111]">Admin only</h1>
        <button onClick={() => navigate('/')} className="mt-4 px-5 py-2.5 rounded-full bg-[#111111] text-white text-sm font-bold">Back home</button>
      </div>
    );
  }

  const g = data?.growth || {};
  const e = data?.engagement || {};
  const t = data?.trust || {};
  const geo = data?.geography || {};
  const c = data?.content || {};

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2] pb-32" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-black/[0.05]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center gap-2 px-3 pt-3 pb-3">
          <button onClick={() => navigate(-1)} data-testid="analytics-back" className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90">
            <ArrowLeft size={18} className="text-[#111111]" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <TrendingUp size={14} className="text-[#3F5200]" />
              <h1 className="text-[16px] font-extrabold text-[#111111] tracking-tight">Business Overview</h1>
            </div>
            {data && (
              <p className="text-[11px] text-[#9B9EA3] -mt-0.5">
                Snapshot · {new Date(data.generated_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            )}
          </div>
          <button onClick={refresh} data-testid="analytics-refresh" className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90">
            <RefreshCw size={16} className={`text-[#111111] ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 pt-5 space-y-6">
        {loading && !data && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
          </div>
        )}

        {data && (
          <>
            {/* Top KPI strip — the headline numbers */}
            <Section label="Headline">
              <div className="grid grid-cols-2 gap-3" data-testid="kpi-strip">
                <Kpi
                  label="Total users"
                  value={g.users?.total}
                  delta={g.users?.last_7d}
                  Icon={Users}
                  tone="lime"
                />
                <Kpi
                  label="Total plans"
                  value={g.plans?.total}
                  delta={g.plans?.last_7d}
                  Icon={Calendar}
                  tone="purple"
                />
                <Kpi
                  label="Stories posted"
                  value={g.stories?.total}
                  delta={g.stories?.last_7d}
                  Icon={ImageIcon}
                  tone="pink"
                />
                <Kpi
                  label="Vibes posted"
                  value={g.vibes?.total}
                  delta={g.vibes?.last_7d}
                  Icon={MessageCircle}
                  tone="blue"
                />
              </div>
            </Section>

            {/* 30-day growth sparklines */}
            <Section label="30-Day Growth">
              <div className="space-y-3" data-testid="growth-sparklines">
                <Spark title="New users / day" data={data.timeseries?.users} total={g.users?.last_30d} />
                <Spark title="New plans / day" data={data.timeseries?.plans} total={g.plans?.last_30d} />
                <Spark title="New stories / day" data={data.timeseries?.stories} />
              </div>
            </Section>

            {/* Engagement */}
            <Section label="Engagement" Icon={ActivityIcon}>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Plan joins" value={e.plan_joins_approved} sub={`${e.plan_joins_last_7d || 0} in 7d`} />
                <MiniStat label="Avg joins / plan" value={e.avg_joins_per_plan} />
                <MiniStat label="Active follows" value={e.follows_active} />
                <MiniStat label="Group chats" value={e.group_chats} />
                <MiniStat label="Pending joins" value={e.plan_joins_pending} />
                <MiniStat label="Activities total" value={e.activities} />
              </div>
            </Section>

            {/* Plan lifecycle */}
            <Section label="Plan Lifecycle">
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Active" value={g.plans?.active} accent="#3F5200" />
                <MiniStat label="Completed" value={g.plans?.completed} />
                <MiniStat label="Cancelled" value={g.plans?.cancelled} accent="#9B9EA3" />
                <MiniStat label="Removed by mods" value={g.plans?.removed} accent="#FF3B30" />
              </div>
            </Section>

            {/* Trust & Safety */}
            <Section label="Trust & Safety" Icon={Shield}>
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Reports total" value={t.reports_total} />
                <MiniStat label="Pending" value={t.reports_pending} accent="#FF3B30" />
                <MiniStat label="Reviewed" value={t.reports_reviewed} />
                <MiniStat label="High severity" value={t.reports_high_severity} accent="#FF3B30" />
                <MiniStat label="Warnings issued" value={t.warnings_issued} accent="#FF9500" />
                <MiniStat label="Suspensions" value={t.suspensions_issued} accent="#FF3B30" />
              </div>
              <button
                onClick={() => navigate('/admin/reports')}
                data-testid="open-admin-reports-from-analytics"
                className="w-full mt-3 py-2.5 rounded-full bg-[#111111] text-white text-[12px] font-bold active:scale-95"
              >
                Open Trust & Safety queue
              </button>
            </Section>

            {/* Geography */}
            <Section label="Geography" Icon={MapPin}>
              <div className="grid grid-cols-2 gap-3">
                <TopList title="Users by city" rows={geo.users_by_city} max={5} />
                <TopList title="Plans by city" rows={geo.plans_by_city} max={5} />
              </div>
            </Section>

            {/* Content mix */}
            <Section label="Content Mix">
              <div className="space-y-3">
                <TopList title="Top categories" rows={c.top_categories} />
                <TopList title="Cost types" rows={c.plans_by_cost_type} />
              </div>
            </Section>

            {/* Top hosts */}
            <Section label="Top Hosts" Icon={Award}>
              <div className="bg-white rounded-2xl border border-black/[0.06] divide-y divide-black/[0.04]" data-testid="top-hosts">
                {(data.top_hosts || []).map((h, i) => (
                  <button
                    key={h.id}
                    onClick={() => navigate(`/user/${h.id}`)}
                    className="w-full flex items-center gap-3 p-3 active:bg-[#F1F0EA] text-left"
                    data-testid={`top-host-${h.id}`}
                  >
                    <span className="w-6 text-center text-xs font-bold text-[#9B9EA3]">{i + 1}</span>
                    {h.profile_photo ? (
                      <img loading="lazy" decoding="async" src={h.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F1F0EA] text-xs font-bold flex items-center justify-center text-[#9B9EA3]">{(h.display_name || '?').slice(0, 1)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#111111] truncate">{h.display_name}</p>
                      <p className="text-[11px] text-[#9B9EA3] truncate">@{h.username}{h.city ? ` · ${h.city}` : ''}</p>
                    </div>
                    <span className="text-sm font-extrabold text-[#3F5200]">{h.hosted_count}</span>
                  </button>
                ))}
                {(data.top_hosts || []).length === 0 && (
                  <p className="p-4 text-xs text-[#9B9EA3] text-center">No hosts yet.</p>
                )}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function Section({ label, Icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-2.5">
        {Icon && <Icon size={13} className="text-[#9B9EA3]" />}
        <p className="text-[10.5px] font-bold uppercase tracking-wider text-[#9B9EA3]">{label}</p>
      </div>
      {children}
    </section>
  );
}

const TONE_BG = {
  lime:   'bg-[#B6E600]/15',
  purple: 'bg-[#5856D6]/12',
  pink:   'bg-[#FF2D95]/12',
  blue:   'bg-[#007AFF]/12',
};
const TONE_FG = {
  lime:   '#3F5200',
  purple: '#3D3BAD',
  pink:   '#BA1F6A',
  blue:   '#0050C7',
};

function Kpi({ label, value, delta, Icon, tone = 'lime' }) {
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-3.5">
      <div className={`w-9 h-9 rounded-xl ${TONE_BG[tone]} flex items-center justify-center mb-2.5`}>
        <Icon size={16} strokeWidth={2.4} color={TONE_FG[tone]} />
      </div>
      <p className="text-[22px] font-extrabold text-[#111111] tracking-tight leading-none tabular-nums">
        {value !== undefined && value !== null ? value : '—'}
      </p>
      <p className="text-[11px] text-[#6B6E73] mt-1">{label}</p>
      {delta !== undefined && delta !== null && (
        <p className="text-[10px] mt-1.5 font-bold" style={{ color: delta > 0 ? '#3F5200' : '#9B9EA3' }}>
          {delta > 0 ? `+${delta}` : delta} in 7d
        </p>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub, accent = '#111111' }) {
  return (
    <div className="bg-white rounded-xl border border-black/[0.06] p-3">
      <p className="text-[18px] font-extrabold tracking-tight tabular-nums" style={{ color: accent }}>
        {value !== undefined && value !== null ? value : '—'}
      </p>
      <p className="text-[11px] text-[#6B6E73] mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-[#9B9EA3] mt-0.5">{sub}</p>}
    </div>
  );
}

function Spark({ title, data, total }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => d.count));
  const W = 280, H = 56, pad = 4;
  const step = (W - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad + i * step;
    const y = H - pad - ((d.count / max) * (H - pad * 2));
    return `${x},${y}`;
  }).join(' ');
  const sum = data.reduce((a, b) => a + b.count, 0);
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-3.5">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[12px] font-bold text-[#111111]">{title}</p>
        <p className="text-[11px] font-bold text-[#3F5200] tabular-nums">
          {total !== undefined ? `${total} in 30d` : `${sum} total`}
        </p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" data-testid={`spark-${title.toLowerCase().split(' ')[0]}`}>
        <polyline points={points} fill="none" stroke="#B6E600" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Last point dot */}
        <circle
          cx={pad + (data.length - 1) * step}
          cy={H - pad - ((data[data.length - 1].count / max) * (H - pad * 2))}
          r="3" fill="#3F5200"
        />
      </svg>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-[#9B9EA3]">{data[0].date.slice(5)}</span>
        <span className="text-[9px] text-[#9B9EA3]">{data[data.length - 1].date.slice(5)}</span>
      </div>
    </div>
  );
}

function TopList({ title, rows, max = 8 }) {
  if (!rows) return null;
  const m = Math.max(1, ...rows.map(r => r.count));
  return (
    <div className="bg-white rounded-2xl border border-black/[0.06] p-3.5">
      <p className="text-[11px] font-bold text-[#111111] mb-2">{title}</p>
      <div className="space-y-1.5">
        {rows.slice(0, max).map((r, i) => {
          const pct = Math.round((r.count / m) * 100);
          return (
            <div key={i}>
              <div className="flex items-center justify-between text-[11.5px]">
                <span className="font-semibold text-[#111111] truncate capitalize">{r.label || '—'}</span>
                <span className="text-[#3F5200] font-bold tabular-nums">{r.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#F1F0EA] overflow-hidden mt-1">
                <div className="h-full bg-[#B6E600] rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="text-[10px] text-[#9B9EA3]">No data yet.</p>
        )}
      </div>
    </div>
  );
}
